import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { onAuthStateChanged, getAuth, User } from "firebase/auth";
import {
  doc,
  getDoc,
  getFirestore,
  collectionGroup,
  query,
  where,
  getDocs,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { app } from "../config/firebaseConfig";
import * as Sentry from "@sentry/browser";
import useUpdateBusiness from "@/hooks/mutations/use-update-business";
import { identifyAmplitudeUser, resetAmplitudeUser } from "@/lib/amplitude";
import {
  identifyOpenPanelUser,
  syncOpenPanelBusinessGroup,
  resetOpenPanelUser,
} from "@/lib/openpanel-identify";
import { track, LogEvents } from "@/analytics/track";
import { BusinessData } from "@/types/businessData";
import { BusinessMember, MembershipRole } from "@/types/businessMember";
import i18n from "@/i18n";
import {
  clearImpersonationAdminUrl,
  getImpersonationAdminUrl,
} from "@/lib/impersonation-admin-url";
import { resolveImpersonationFromToken } from "@/lib/impersonation";
import type {
  MembershipAccessLevel,
  MembershipPermissions,
} from "@/types/businessMember";
import {
  effectiveAccessLevel,
  effectiveStoreRole,
  getBusinessVertical,
  type StoreRole,
} from "@graftus/schema";
import { applyVerticalTerminology } from "@/i18n/vertical-terminology";

/** localStorage key for the user's chosen active business id */
export const GRAFTUS_ACTIVE_BUSINESS_ID_KEY = "graftus_activeBusinessId";

export interface ImpersonationInfo {
  impersonatedBy: string | null;
  impersonatedByEmail: string | null;
  impersonatedBusiness: string | null;
}

/** A business the signed-in user can switch to, with display name for the switcher. */
export interface BusinessMembershipOption {
  businessId: string;
  businessName: string;
  role: MembershipRole;
  accessLevel: MembershipAccessLevel;
  permissions: MembershipPermissions;
}

interface ProfileContextProps {
  businessProfile: BusinessData | null;
  memberProfile: BusinessMember | null;
  memberRole: MembershipRole | null;
  memberAccessLevel: MembershipAccessLevel;
  /** Report capabilities of the active membership (GRA-96). */
  memberPermissions: MembershipPermissions;
  /**
   * The platform sketch's 4-role model (GRA-282), derived from `memberRole`/
   * `memberAccessLevel` plus any stored override via `effectiveStoreRole` -
   * never a second source of truth. Route/UI guards and "Visa som" key off
   * this, not the legacy fields.
   */
  memberStoreRole: StoreRole;
  /** All businesses the user is an active member of (for the company switcher). */
  memberships: BusinessMembershipOption[];
  /** Currently selected business id, or null when none is resolved. */
  activeBusinessId: string | null;
  /** Switch the active business; no-op if already active or not a member. */
  switchBusiness: (businessId: string) => Promise<void>;
  loading: boolean;
  inactiveUser: boolean;
  onboardingComplete: boolean;
  setProfileCreated?: (created: boolean) => void;
  unreadMessages: number;
  setUnreadMessages: (unreadMessages: number) => void;
  signInAsBusinessMember: (businessUid: string) => Promise<void>;
  signOutAsBusinessMember: () => Promise<void>;
  /** Re-load member + business from Firestore (e.g. after accepting an invite without auth state change). */
  refreshProfile: () => Promise<void>;
  isSignedInAsBusinessMember: boolean;
  isImpersonating: boolean;
  impersonationInfo: ImpersonationInfo | null;
  endImpersonation: () => Promise<void>;
  emailPreferences?: {
    notifyOnMatches: boolean;
  };
  toggleNotifyOnMatches: (enabled: boolean) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextProps | undefined>(
  undefined
);

interface ProfileProviderProps {
  children: ReactNode;
}

interface MembershipEntry {
  businessId: string;
  role: MembershipRole;
  status: string;
  accessLevel: MembershipAccessLevel;
  permissions: MembershipPermissions;
  /** Stored override, if any; `effectiveStoreRole` derives the rest. */
  storeRole?: StoreRole;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
}) => {
  const [businessProfile, setBusinessProfile] = useState<BusinessData | null>(
    null
  );
  const [memberProfile, setMemberProfile] = useState<BusinessMember | null>(
    null
  );
  const [memberRole, setMemberRole] = useState<MembershipRole | null>(null);
  const [memberAccessLevel, setMemberAccessLevel] = useState<MembershipAccessLevel>("full");
  const [memberPermissions, setMemberPermissions] = useState<MembershipPermissions>({});
  const [memberStoreRoleOverride, setMemberStoreRoleOverride] = useState<
    StoreRole | undefined
  >(undefined);
  const [memberships, setMemberships] = useState<BusinessMembershipOption[]>([]);
  const [activeBusinessId, setActiveBusinessId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inactiveUser, setInactiveUser] = useState<boolean>(false);
  const [profileCreated, setProfileCreated] = useState<boolean>(false);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  const [isSignedInAsBusinessMember, setIsSignedInAsBusinessMember] =
    useState<boolean>(false);
  const [impersonationInfo, setImpersonationInfo] =
    useState<ImpersonationInfo | null>(null);

  const auth = getAuth(app);
  const db = getFirestore(app);
  const updateBusiness = useUpdateBusiness();
  /** Avoid emitting SignOut on initial `onAuthStateChanged(null)` when the user was never signed in. */
  const hadAuthenticatedUserRef = useRef(false);
  /** Monotonic counter — incremented on every `onAuthStateChanged` call so
   *  stale async continuations can detect they've been superseded. */
  const authStateVersionRef = useRef(0);

  const signInAsBusinessMember = async (businessUid: string) => {
    if (!businessProfile?.isAdmin) {
      return;
    }
    try {
      const businessRef = doc(db, "Businesses", businessUid);
      const businessSnap = await getDoc(businessRef);

      if (businessSnap.exists()) {
        const businessData = businessSnap.data() as BusinessData;
        setBusinessProfile(businessData);
        setIsSignedInAsBusinessMember(true);
        identifyAmplitudeUser(auth.currentUser?.uid ?? "", businessData.uid, {
          email: memberProfile?.email,
          name: memberProfile?.name,
        });
        identifyOpenPanelUser(auth.currentUser?.uid ?? "", businessData.uid, {
          email: memberProfile?.email,
          name: memberProfile?.name,
          viewAsAdmin: true,
        });
        syncOpenPanelBusinessGroup(businessData);
        track({
          event: LogEvents.ViewAsBusinessMember.name,
          channel: LogEvents.ViewAsBusinessMember.channel,
          business_uid: businessData.uid,
        });
      }
    } catch (error) {
      console.error("Error signing in as business member: ", error);
    }
  };

  const signOutAsBusinessMember = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setIsSignedInAsBusinessMember(false);
    const snapshotVersion = authStateVersionRef.current;
    const snapshotUid = user.uid;
    const isCurrent = () =>
      authStateVersionRef.current === snapshotVersion &&
      auth.currentUser?.uid === snapshotUid;
    await fetchBusinessMemberAndProfile(user, isCurrent);
  };

  const endImpersonation = async () => {
    // Clear persisted impersonation claims on the member's account before
    // signing out, so they don't leak into the member's own future sessions.
    // The admin dashboard origin is captured at impersonation start
    // (handoff URL) and persisted in localStorage, with a fallback to the
    // build-time VITE_ADMIN_DASHBOARD_URL env var.
    const adminUrl = getImpersonationAdminUrl();
    const user = auth.currentUser;

    if (user) {
      if (adminUrl) {
        try {
          const idToken = await user.getIdToken();
          const response = await fetch(`${adminUrl}/api/impersonate/stop`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ memberUid: user.uid }),
          });
          if (!response.ok) {
            console.error(
              `endImpersonation: stop request failed (${response.status} ${response.statusText}). ` +
                "Impersonation claims may remain on the member's account.",
            );
          }
        } catch (error) {
          console.error(
            "endImpersonation: failed to clear server claims",
            error,
          );
        }
      } else {
        console.error(
          "endImpersonation: no admin dashboard URL is known (handoff URL " +
            "did not include `adminUrl` and VITE_ADMIN_DASHBOARD_URL is not " +
            "configured). Impersonation claims will remain on the member's " +
            "Firebase Auth account until cleared manually.",
        );
      }
    }

    // Drop persisted handoff URL now that we are done with it (success or
    // failure). Must not rely on onAuthStateChanged(null) for this, because
    // that also runs during impersonation handoff signOut.
    clearImpersonationAdminUrl();

    setImpersonationInfo(null);
    setIsSignedInAsBusinessMember(false);
    try {
      await auth.signOut();
    } catch (error) {
      console.error("endImpersonation: signOut failed", error);
    }
    if (typeof window !== "undefined") {
      window.location.href = adminUrl ?? "/sign-in";
    }
  };

  async function detectImpersonation(user: User): Promise<ImpersonationInfo | null> {
    try {
      // Force-refresh so claims match the server. Cached tokens can lag after
      // `setCustomUserClaims` (e.g. impersonate/stop clearing claims mid-session)
      // or when impersonation is set while the user is already signed in.
      const tokenResult = await user.getIdTokenResult(true);
      // Gate strictly on the sign-in provider: only the admin handoff
      // (signInWithCustomToken → "custom") may surface impersonation. This
      // prevents stale impersonation claims from leaking into the member's own
      // credential-based session (GRA-21).
      return resolveImpersonationFromToken({
        claims: tokenResult.claims as Record<string, unknown>,
        signInProvider: tokenResult.signInProvider,
      });
    } catch (error) {
      console.warn("detectImpersonation: failed to read token claims", error);
      return null;
    }
  }

  const fetchBusinessMemberAndProfile = useCallback(
    async (user: User, isCurrent: () => boolean) => {
      let businessUidForAmplitude: string | null = null;
      let businessDataForOpenPanel: BusinessData | null = null;
      let memberRoleForOpenPanel: MembershipRole | null = null;
      let emailForAmplitude: string | undefined;
      let nameForAmplitude: string | undefined;

      setLoading(true);

      try {
        const memberRef = doc(db, "BusinessMember", user.uid);
        const memberSnap = await getDoc(memberRef);
        if (!isCurrent()) return;

        let profile: BusinessMember;

        if (memberSnap.exists()) {
          profile = memberSnap.data() as BusinessMember;
        } else {
          const now = serverTimestamp();
          profile = {
            uid: user.uid,
            name: user.displayName || "",
            email: user.email || "",
            createdAt: now as any,
            updatedAt: now as any,
          };
          await setDoc(memberRef, profile);
          if (!isCurrent()) return;
        }

        setMemberProfile(profile);
        emailForAmplitude = profile.email;
        nameForAmplitude = profile.name;

        // Account preference wins over browser/localStorage detection.
        if (profile.language && profile.language !== i18n.resolvedLanguage) {
          void i18n.changeLanguage(profile.language);
        }

        Sentry.setUser({ id: profile.uid, email: profile.email });

        const memberships = await findActiveMemberships(user.uid);
        if (!isCurrent()) return;

        if (memberships.length > 0) {
          // Fetch every membership's business doc once so we can both render the
          // switcher (needs display names) and select the active one without a
          // second read.
          const businessSnaps = await Promise.all(
            memberships.map((m) => getDoc(doc(db, "Businesses", m.businessId)))
          );
          if (!isCurrent()) return;

          const businessDataById = new Map<string, BusinessData>();
          const membershipOptions: BusinessMembershipOption[] = [];
          memberships.forEach((m, i) => {
            const snap = businessSnaps[i];
            if (!snap?.exists()) return;
            const data = snap.data() as BusinessData;
            businessDataById.set(m.businessId, data);
            membershipOptions.push({
              businessId: m.businessId,
              businessName: data.business || data.name || "Graftus",
              role: m.role,
              accessLevel: m.accessLevel,
              permissions: m.permissions,
            });
          });
          setMemberships(membershipOptions);

          const savedId = localStorage.getItem(GRAFTUS_ACTIVE_BUSINESS_ID_KEY);
          const chosen =
            memberships.find(
              (m) => m.businessId === savedId && businessDataById.has(m.businessId)
            ) ?? memberships.find((m) => businessDataById.has(m.businessId));

          if (chosen) {
            const businessData = businessDataById.get(chosen.businessId)!;
            setBusinessProfile(businessData);
            setMemberRole(chosen.role);
            setMemberAccessLevel(chosen.accessLevel);
            setMemberPermissions(chosen.permissions);
            setMemberStoreRoleOverride(chosen.storeRole);
            setActiveBusinessId(chosen.businessId);
            businessUidForAmplitude = businessData.uid;
            businessDataForOpenPanel = businessData;
            memberRoleForOpenPanel = chosen.role;
            localStorage.setItem(GRAFTUS_ACTIVE_BUSINESS_ID_KEY, chosen.businessId);
          } else {
            setBusinessProfile(null);
            setMemberRole(null);
            setMemberAccessLevel("full");
            setMemberPermissions({});
            setMemberStoreRoleOverride(undefined);
            setActiveBusinessId(null);
          }
        } else {
          const legacyResult = await tryLegacyOwnerFallback(user, profile);
          if (!isCurrent()) return;

          if (legacyResult) {
            setBusinessProfile(legacyResult.business);
            setMemberRole("owner");
            setMemberAccessLevel("full");
            setMemberPermissions({});
            setMemberStoreRoleOverride(undefined);
            setActiveBusinessId(legacyResult.business.uid);
            setMemberships([
              {
                businessId: legacyResult.business.uid,
                businessName:
                  legacyResult.business.business ||
                  legacyResult.business.name ||
                  "Graftus",
                role: "owner",
                accessLevel: "full",
                permissions: {},
              },
            ]);
            businessUidForAmplitude = legacyResult.business.uid;
            businessDataForOpenPanel = legacyResult.business;
            memberRoleForOpenPanel = "owner";
            localStorage.setItem(
              GRAFTUS_ACTIVE_BUSINESS_ID_KEY,
              legacyResult.business.uid
            );
          } else {
            setBusinessProfile(null);
            setMemberRole(null);
            setMemberStoreRoleOverride(undefined);
            setMemberships([]);
            setActiveBusinessId(null);
          }
        }

        identifyAmplitudeUser(user.uid, businessUidForAmplitude, {
          email: emailForAmplitude,
          name: nameForAmplitude,
        });
        identifyOpenPanelUser(user.uid, businessUidForAmplitude, {
          email: emailForAmplitude,
          name: nameForAmplitude,
          memberRole: memberRoleForOpenPanel ?? undefined,
        });
        if (businessDataForOpenPanel) {
          syncOpenPanelBusinessGroup(businessDataForOpenPanel);
        }
      } catch (error) {
        console.error("Error fetching profile data: ", error);
      } finally {
        if (isCurrent()) {
          setLoading(false);
        }
      }
    },
    [db]
  );

  const refreshProfile = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    const snapshotVersion = authStateVersionRef.current;
    const snapshotUid = user.uid;
    const isCurrent = () =>
      authStateVersionRef.current === snapshotVersion &&
      auth.currentUser?.uid === snapshotUid;
    await fetchBusinessMemberAndProfile(user, isCurrent);
  }, [auth, fetchBusinessMemberAndProfile]);

  const switchBusiness = async (businessId: string) => {
    if (businessId === activeBusinessId) return;
    if (!memberships.some((m) => m.businessId === businessId)) return;
    localStorage.setItem(GRAFTUS_ACTIVE_BUSINESS_ID_KEY, businessId);
    await refreshProfile();
  };

  async function findActiveMemberships(uid: string): Promise<MembershipEntry[]> {
    try {
      const membershipsQuery = query(
        collectionGroup(db, "members"),
        where("uid", "==", uid)
      );
      const snap = await getDocs(membershipsQuery);

      return snap.docs
        .map((d) => {
          const data = d.data();
          const role = (data.role as MembershipRole) ?? "member";
          const accessLevel: MembershipAccessLevel = effectiveAccessLevel({
            role,
            accessLevel: data.accessLevel as string | undefined,
          });
          const permissions =
            (data.permissions as MembershipPermissions | undefined) ?? {};
          const storeRole = data.storeRole as StoreRole | undefined;
          return {
            businessId: d.ref.parent.parent?.id ?? "",
            role,
            status: (data.status as string) ?? "active",
            accessLevel,
            permissions,
            storeRole,
          };
        })
        .filter((m) => m.businessId && m.status !== "removed");
    } catch {
      return [];
    }
  }

  async function tryLegacyOwnerFallback(
    user: User,
    profile: BusinessMember
  ): Promise<{ business: BusinessData } | null> {
    const businessRef = doc(db, "Businesses", user.uid);
    const businessSnap = await getDoc(businessRef);

    if (!businessSnap.exists()) return null;

    const businessData = businessSnap.data() as BusinessData;

    const membershipRef = doc(
      db,
      "Businesses",
      user.uid,
      "members",
      user.uid
    );
    try {
      const membershipSnap = await getDoc(membershipRef);
      if (!membershipSnap.exists()) {
        await setDoc(membershipRef, {
          uid: user.uid,
          name: profile.name || businessData.name || "",
          email: profile.email || businessData.email || "",
          role: "owner",
          status: "active",
          joinedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Failed to create legacy membership doc", error);
    }

    return { business: businessData };
  }

  const toggleNotifyOnMatches = async (enabled: boolean) => {
    if (!businessProfile) return;
    try {
      const newEmailPreferences = {
        ...(businessProfile.emailPreferences || {}),
        notifyOnMatches: enabled,
      };
      await updateBusiness.mutateAsync({
        id: businessProfile.uid,
        fields: { emailPreferences: newEmailPreferences },
      });
      setBusinessProfile({
        ...businessProfile,
        emailPreferences: newEmailPreferences,
      });
    } catch (error) {
      console.error("Error updating matches notification setting: ", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const version = ++authStateVersionRef.current;
      const isCurrent = () => authStateVersionRef.current === version;

      if (user) {
        hadAuthenticatedUserRef.current = true;
        setLoading(true);
        const info = await detectImpersonation(user);
        if (!isCurrent()) return;

        if (info) {
          setImpersonationInfo(info);
          setIsSignedInAsBusinessMember(true);
          if (info.impersonatedBusiness) {
            localStorage.setItem(
              GRAFTUS_ACTIVE_BUSINESS_ID_KEY,
              info.impersonatedBusiness
            );
          }
        } else {
          setImpersonationInfo(null);
          // Token has no impersonation claims — drop any stale admin URL
          // left over from a previous (already-ended) impersonation session.
          clearImpersonationAdminUrl();
        }
        fetchBusinessMemberAndProfile(user, isCurrent);
      } else {
        setLoading(false);
        setMemberProfile(null);
        setBusinessProfile(null);
        setMemberRole(null);
        setMemberAccessLevel("full");
        setMemberPermissions({});
        setMemberStoreRoleOverride(undefined);
        setMemberships([]);
        setActiveBusinessId(null);
        setImpersonationInfo(null);
        setIsSignedInAsBusinessMember(false);
        localStorage.removeItem(GRAFTUS_ACTIVE_BUSINESS_ID_KEY);
        // Do not call clearImpersonationAdminUrl() here: ImpersonateView runs
        // signOut() immediately before signInWithCustomToken during the
        // handoff, and we must keep the persisted admin origin until
        // endImpersonation (or until a signed-in user has no impersonation
        // claims — handled above).
        if (hadAuthenticatedUserRef.current) {
          hadAuthenticatedUserRef.current = false;
          track({
            event: LogEvents.SignOut.name,
            channel: LogEvents.SignOut.channel,
          });
        }
        resetAmplitudeUser();
        resetOpenPanelUser();
        Sentry.setUser(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [auth, fetchBusinessMemberAndProfile, profileCreated]);

  useEffect(() => {
    if (!businessProfile) return;

    const subscriptionStatus = businessProfile.subscription?.status;
    const hasActiveSubscription =
      subscriptionStatus === "active" || subscriptionStatus === "trialing";

    if (businessProfile.subscription) {
      setInactiveUser(!hasActiveSubscription);
      return;
    }

    const legacyActive =
      businessProfile.sub_still_active === "active" || businessProfile.trial;
    setInactiveUser(!legacyActive);
  }, [businessProfile]);

  // GRA-83: a grocery store reads DVH terms everywhere (avdelning,
  // medarbetare, butiksdrift); switching business switches them back.
  const vertical = getBusinessVertical(businessProfile);
  useEffect(() => {
    applyVerticalTerminology(vertical);
  }, [vertical]);

  const onboardingComplete =
    businessProfile?.companyProfile?.completedAt != null;

  // The 4-role model (GRA-282): derived, never a second source of truth.
  const memberStoreRole = effectiveStoreRole({
    role: memberRole,
    accessLevel: memberAccessLevel,
    storeRole: memberStoreRoleOverride,
  });

  return (
    <ProfileContext.Provider
      value={{
        businessProfile,
        memberProfile,
        memberRole,
        memberAccessLevel,
        memberPermissions,
        memberStoreRole,
        memberships,
        activeBusinessId,
        switchBusiness,
        loading,
        inactiveUser,
        onboardingComplete,
        setProfileCreated,
        unreadMessages,
        setUnreadMessages,
        signInAsBusinessMember,
        signOutAsBusinessMember,
        refreshProfile,
        isSignedInAsBusinessMember,
        isImpersonating: impersonationInfo !== null,
        impersonationInfo,
        endImpersonation,
        emailPreferences: businessProfile?.emailPreferences || {
          notifyOnMatches: false,
        },
        toggleNotifyOnMatches,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};
